import type { CommonErrorEventMap, CommonInfoEventMap, CommonWarnEventMap } from '../actions/common'
import type { PerfPluginConfig } from '../plugins/perf/config'
import type { LoggerConfig } from './types'

/**
 * @fileoverview createLogger 工厂函数
 *
 * 接入方统一入口。三泛型分别绑定 warn/error/info 的 EventMap，
 * 调用处 action↔data 形状编译期强校验。
 *
 * @example
 * ```ts
 * import { createLogger } from '@peerup/browser-logger'
 * import type { ErrorEventMap, WarnEventMap } from './types'
 *
 * export const $logger = createLogger<WarnEventMap, ErrorEventMap>({
 *   source: 'web',
 *   appVersion: __APP_VERSION__,
 *   isDev: import.meta.env.DEV,
 *   allowLog: () => ERROR_REPORT && !IS_DEV,
 *   tracker: envConfig.TRACKER_CONFIG,
 * })
 *
 * // 类型安全：action 自动补全，data 形状由 action 决定
 * $logger.error('EDITOR_WS_ERROR', { error: new Error('x'), docId: '123' }) // ✅
 * $logger.error('EDITOR_WS_ERROR', { wrong: true }) // ❌ data 不匹配
 * ```
 */
import { AliWebLogger } from './AliWebLogger'

/**
 * 模块级默认 logger 实例
 * @description createLogger 创建的实例自动注册为默认实例，
 *   供 useLoggerHook / useScrollFPSObserver 等 composable 在不显式传 logger 时使用。
 *   后创建的实例覆盖先创建的（单页应用通常只有一个 logger）。
 */
let defaultLogger: AliWebLogger | null = null

/** 获取默认 logger 实例（composable 内部使用） */
export function getDefaultLogger(): AliWebLogger | null {
  return defaultLogger
}

/**
 * 创建 logger 实例
 * @param config 接入方配置
 * @returns logger 实例（warn/error/info 方法已绑定 EventMap 泛型）
 */
export function createLogger<
  TWarnMap extends CommonWarnEventMap = CommonWarnEventMap,
  TErrorMap extends CommonErrorEventMap = CommonErrorEventMap,
  TInfoMap extends CommonInfoEventMap = CommonInfoEventMap,
>(config: LoggerConfig<TWarnMap, TErrorMap, TInfoMap>): AliWebLogger<TWarnMap, TErrorMap, TInfoMap> {
  // micro-app 复用：直接返回外部注入的实例，避免重复实例化/重复上报
  // externalInstance 是主应用的 logger（默认泛型），子应用声明自己的泛型仅为类型提示
  if (config.externalInstance) {
    defaultLogger = config.externalInstance
    return config.externalInstance as AliWebLogger<TWarnMap, TErrorMap, TInfoMap>
  }

  const logger = new AliWebLogger<TWarnMap, TErrorMap, TInfoMap>(config)

  // 按需注入 perf 插件（动态导入，不传 perf 配置则完全不加载 perf 代码）
  // perf: true → 全默认开启；perf: {...} → 定制配置；不传 → 不启用
  const perfRaw = config.plugins?.perf
  if (perfRaw) {
    const baseConfig: PerfPluginConfig = perfRaw === true ? {} : perfRaw
    // 把 staticUrl 增量并入资源采集白名单（对齐旧实现 allowOrigin 含 envConfig.STATIC_URL）
    // 注意 spread 顺序：baseConfig 在前、resourceOrigins 在后，避免用户的 resourceOrigins 被 staticUrl 覆盖
    const perfConfig: PerfPluginConfig = config.staticUrl
      ? { ...baseConfig, resourceOrigins: [config.staticUrl, ...(baseConfig.resourceOrigins ?? [])] }
      : baseConfig
    void import('../plugins/perf/index').then(({ createPerfController }) => {
      logger.setPerfController(createPerfController(perfConfig, config.isDev ?? false))
    })
  }

  // 注册为默认 logger（供 composable 使用，泛型擦除为默认泛型）
  defaultLogger = logger as AliWebLogger

  return logger
}
